import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Palette,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Upload,
  Loader2,
  Image as ImageIcon,
  Check,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import api from '@/services/api';

interface Estampado {
  id: string;
  name: string;
  path: string;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
}

const EstampadosManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Estampado | null>(null);
  const [newEstampado, setNewEstampado] = useState({ name: '', file: null as File | null });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Cleanup on unmount to prevent React DOM errors
  useEffect(() => {
    return () => {
      // Clean up states when component unmounts
      setIsCreateModalOpen(false);
      setDeleteTarget(null);
      setPreviewUrl(null);
    };
  }, []);

  // Fetch estampados
  const { data, isLoading, error } = useQuery({
    queryKey: ['estampados-admin'],
    queryFn: async () => {
      const response = await api.get('/estampados');
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await api.post('/estampados', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      // Reset states first
      setNewEstampado({ name: '', file: null });
      setPreviewUrl(null);

      // Close modal after a tiny delay to prevent React DOM errors
      setTimeout(() => {
        setIsCreateModalOpen(false);
      }, 50);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['estampados-admin'] });
      queryClient.invalidateQueries({ queryKey: ['estampados'] });

      toast({
        title: 'Estampado creado',
        description: 'El nuevo estampado se ha agregado exitosamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'No se pudo crear el estampado',
        variant: 'destructive',
      });
    },
  });

  // Update mutation (toggle active)
  const updateMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const response = await api.put(`/estampados/${id}`, { active });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estampados-admin'] });
      queryClient.invalidateQueries({ queryKey: ['estampados'] });
      toast({
        title: 'Estampado actualizado',
        description: 'El estado del estampado se ha actualizado',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'No se pudo actualizar el estampado',
        variant: 'destructive',
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/estampados/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estampados-admin'] });
      queryClient.invalidateQueries({ queryKey: ['estampados'] });
      setDeleteTarget(null);
      toast({
        title: 'Estampado eliminado',
        description: 'El estampado se ha eliminado exitosamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'No se pudo eliminar el estampado',
        variant: 'destructive',
      });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewEstampado({ ...newEstampado, file });
      // Generate preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreate = () => {
    if (!newEstampado.name || !newEstampado.file) {
      toast({
        title: 'Campos requeridos',
        description: 'Debes proporcionar un nombre y una imagen',
        variant: 'destructive',
      });
      return;
    }

    const formData = new FormData();
    formData.append('name', newEstampado.name);
    formData.append('image', newEstampado.file);

    createMutation.mutate(formData);
  };

  const handleToggleActive = (estampado: Estampado) => {
    updateMutation.mutate({
      id: estampado.id,
      active: !estampado.active,
    });
  };

  const handleDelete = (estampado: Estampado) => {
    setDeleteTarget(estampado);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Gestión de Estampados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-brown" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Gestión de Estampados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-red-600">
            Error al cargar estampados
          </div>
        </CardContent>
      </Card>
    );
  }

  const estampados: Estampado[] = data?.estampados || [];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Gestión de Estampados
              </CardTitle>
              <CardDescription className="mt-2">
                Administra los diseños de portadas disponibles para las agendas personalizadas
              </CardDescription>
            </div>
            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brown hover:bg-brown/90 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Estampado
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Nuevo Estampado</DialogTitle>
                  <DialogDescription>
                    Sube una nueva imagen de estampado para las portadas
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre del Estampado</Label>
                    <Input
                      id="name"
                      placeholder="Ej: Flores Rosadas"
                      value={newEstampado.name}
                      onChange={(e) =>
                        setNewEstampado({ ...newEstampado, name: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image">Imagen</Label>
                    <div className="flex flex-col gap-3">
                      <Input
                        id="image"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleFileChange}
                      />
                      <p className="text-xs text-muted-foreground">
                        Formatos permitidos: JPG, JPEG, PNG (máx. 5MB)
                      </p>
                    </div>
                  </div>

                  {previewUrl && (
                    <div className="space-y-2">
                      <Label>Vista Previa</Label>
                      <div className="border-2 border-dashed rounded-lg p-4 flex items-center justify-center bg-gray-50">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="max-h-48 object-contain rounded"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCreateModalOpen(false);
                      setNewEstampado({ name: '', file: null });
                      setPreviewUrl(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={createMutation.isPending}
                    className="bg-brown hover:bg-brown/90 text-white"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Crear Estampado
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <span>Total: {estampados.length} estampados</span>
              <span>
                Activos: {estampados.filter((e) => e.active).length}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {estampados.map((estampado) => (
                <Card key={estampado.id} className="overflow-hidden">
                  <div className="relative aspect-video bg-gray-100">
                    <img
                      src={estampado.path}
                      alt={estampado.name}
                      className="w-full h-full object-cover"
                    />
                    {!estampado.active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge variant="secondary" className="text-xs">
                          Inactivo
                        </Badge>
                      </div>
                    )}
                  </div>

                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-sm">{estampado.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          ID: {estampado.id}
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={estampado.active}
                            onCheckedChange={() => handleToggleActive(estampado)}
                            disabled={updateMutation.isPending}
                          />
                          <Label className="text-xs cursor-pointer">
                            {estampado.active ? (
                              <span className="flex items-center gap-1 text-green-600">
                                <Eye className="h-3 w-3" /> Visible
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-gray-500">
                                <EyeOff className="h-3 w-3" /> Oculto
                              </span>
                            )}
                          </Label>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(estampado)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {estampados.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>No hay estampados registrados</p>
                <p className="text-sm mt-2">Comienza agregando tu primer estampado</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar estampado?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El estampado "{deleteTarget?.name}" será eliminado permanentemente del sistema.
              {deleteTarget && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <img
                    src={deleteTarget.path}
                    alt={deleteTarget.name}
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EstampadosManager;
